import { useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useApi, useFetch, useMe, errMsg, fmtDate } from "../lib/api";
import { Btn, Card, Empty, PageHeader, Sel, StateBadge } from "../components/common";

export default function Inbox() {
  const api = useApi();
  const { isAdmin } = useMe();
  const [dir, setDir] = useState("");
  const { data, reload } = useFetch(`/emails?direction=${dir}`);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const sync = async () => {
    setBusy(true);
    try { const r = await api.post("/emails/sync"); toast.success(`Gmail connected. ${r.new} new message(s), ${r.enquiry_leads || 0} new enquiry lead(s), ${r.sent_detected} sent draft(s) matched.`); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const draft = async (id) => {
    setBusy(true);
    try { await api.post(`/emails/${id}/draft-reply`); toast.success("Reply draft created — review in Outreach"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div>
      <PageHeader title="Email Inbox" subtitle="Read-only Gmail sync of mail sent to contact@ (forwarded by Cloudflare), bounces, and your sent approved drafts. Follow-ups stop automatically on reply, decline, opt-out, bounce or meeting.">
        <Sel testid="inbox-direction" value={dir} onChange={setDir} placeholder="All mail" options={[{ value: "inbound", label: "Received" }, { value: "outbound", label: "Sent" }]} />
        {isAdmin && <Btn onClick={sync} disabled={busy} data-testid="inbox-sync"><RefreshCw size={14} /> Sync mailbox</Btn>}
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-2" testid="inbox-list">
          {!data?.length ? <Empty text="No client emails yet. Website enquiries and bookings, mail sent to contact@ (last 30 days), bounces and sent outreach appear here after a sync." testid="inbox-empty" /> : data.map((e) => (
            <button key={e.id} data-testid={`email-${e.id}`} onClick={() => setSel(e)} className={`block w-full border-b border-slate-100 px-2 py-2.5 text-left transition-colors hover:bg-slate-50 ${sel?.id === e.id ? "bg-blue-50" : ""}`}>
              <div className="flex justify-between gap-2 text-sm"><span className="truncate font-medium">{e.direction === "inbound" ? e.from_name || e.from : `To: ${e.to}`}</span><StateBadge s={e.classification || e.status} /></div>
              <div className="truncate text-xs text-slate-600">{e.subject}</div><div className="text-[11px] text-slate-400">{fmtDate(e.at)}</div>
            </button>))}
        </Card>
        <Card className="lg:col-span-3" testid="email-view">
          {!sel ? <Empty text="Select a message" /> : (
            <div>
              <h3 className="font-display text-lg font-semibold">{sel.subject}</h3>
              <div className="mb-3 text-xs text-slate-500">{sel.from} → {sel.to} · {fmtDate(sel.at)} {sel.opportunity_id && <Link className="ml-2 text-blue-700 hover:underline" to={`/admin/opportunities/${sel.opportunity_id}`}>linked lead →</Link>}</div>
              <pre className="max-h-[520px] overflow-y-auto whitespace-pre-wrap font-sans text-sm">{sel.body}</pre>
              {isAdmin && sel.direction === "inbound" && <Btn className="mt-4" disabled={busy} onClick={() => draft(sel.id)} data-testid="email-draft-reply"><Sparkles size={14} /> Draft reply with AI</Btn>}
            </div>)}
        </Card>
      </div>
    </div>
  );
}
